import React from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import DocumentTitleSync from './components/DocumentTitleSync';
import AutoEllipsisTitle from './components/AutoEllipsisTitle';
import WorkbenchNavHandoffBootstrap from './pages/ipe/workbench/components/WorkbenchNavHandoffBootstrap';
import RoutePageSuspense from './components/ipe/RoutePageSuspense';
import { IpeAuthProvider } from './context/IpeAuthContext';
import { WorkspaceSwitchLockProvider } from './pages/ipe/workbench/hooks/useWorkspaceSwitchLock';
import IpeRequireAuth from './components/ipe/IpeRequireAuth';
import IpeLoginPage from './pages/ipe/IpeLoginPage';
import TiktokAuthCodePage from './pages/ipe/TiktokAuthCodePage';
import PodOAuthAuthorizePage from './pages/ipe/PodOAuthAuthorizePage';
import { LegalPrivacyPage, LegalTermsPage } from './pages/legal/LegalDocumentPage';
import GeekbiHotSaleRedirect from './pages/ipe/insights/GeekbiHotSaleRedirect';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminCommerceLayout from './pages/admin/AdminCommerceLayout';
import AdminOfficialDataLayout from './pages/admin/AdminOfficialDataLayout';
import AdminOfficialMaterialsLayout from './pages/admin/AdminOfficialMaterialsLayout';
import AgentPortalLoginPage from './pages/agent-portal/AgentPortalLoginPage';
import CreditConsumptionPage from './pages/ipe/CreditConsumptionPage';
import IpeRechargePage from './pages/ipe/IpeRechargePage';
import PluginInstallPage from './pages/ipe/PluginInstallPage';
import TutorialManualLayout from './pages/tutorial/TutorialManualLayout';
import TutorialManualHomePage from './pages/tutorial/TutorialManualHomePage';
import TutorialManualNodePage from './pages/tutorial/TutorialManualNodePage';
import TutorialManualLoginPage from './pages/tutorial/TutorialManualLoginPage';
import { SheinWorkspaceAuthProvider } from '@/shein-platform/context/SheinWorkspaceAuthContext';
import SheinPlatformGatePage from '@/shein-platform/layout/SheinPlatformGatePage';
import SheinPlatformLayout from '@/shein-platform/layout/SheinPlatformLayout';
import SheinProductEditorLayout from '@/shein-platform/layout/SheinProductEditorLayout';
import SheinStandaloneHelpLayout from '@/shein-platform/layout/SheinStandaloneHelpLayout';
import SheinWorkspaceGuard from '@/shein-platform/components/SheinWorkspaceGuard';
import ProductOpsLayout from '@/shein-platform/modules/products/ProductOpsLayout';
import ProductListPage from '@/shein-platform/modules/products/ProductListPage';
import ProductPublishRecordsPage from '@/shein-platform/modules/products/ProductPublishRecordsPage';
import ProductDraftsPage from '@/shein-platform/modules/products/ProductDraftsPage';
import ProductPublishWizardPage from '@/shein-platform/modules/products/ProductPublishWizardPage';
import ProductEditPage from '@/shein-platform/modules/products/ProductEditPage';
import SheinHomePage from '@/shein-platform/modules/home/SheinHomePage';
import DiscussHubPage from '@/shein-platform/modules/discuss/DiscussHubPage';
import DiscussListPage from '@/shein-platform/modules/discuss/DiscussListPage';
import RrpListPage from '@/shein-platform/modules/discuss/RrpListPage';
import ProductQualificationPage from '@/shein-platform/modules/compliance/ProductQualificationPage';
import ComplianceMaterialsPage from '@/shein-platform/modules/compliance/ComplianceMaterialsPage';
import CertificateLibraryPage from '@/shein-platform/modules/certificates/CertificateLibraryPage';
import SizeChartTemplatePage from '@/shein-platform/modules/templates/SizeChartTemplatePage';
import ProductAttributeTemplatePage from '@/shein-platform/modules/templates/ProductAttributeTemplatePage';
import PhotoTemplatePage from '@/shein-platform/modules/templates/PhotoTemplatePage';
import RrpTemplatePage from '@/shein-platform/modules/templates/RrpTemplatePage';
import ProductTemplatePage from '@/shein-platform/modules/templates/ProductTemplatePage';
import ProductTemplateEditorPage from '@/shein-platform/modules/templates/ProductTemplateEditorPage';
import SheinNoticeHelpPage from '@/shein-platform/modules/help/SheinNoticeHelpPage';
import ShopInfoPage from '@/shein-platform/modules/shop/ShopInfoPage';
import ReturnOrderListPage from '@/shein-platform/modules/aftersales/ReturnOrderListPage';
import ReturnOrderDetailPage from '@/shein-platform/modules/aftersales/ReturnOrderDetailPage';
import OrderListPage from '@/shein-platform/modules/aftersales/OrderListPage';
import OrderDetailPage from '@/shein-platform/modules/aftersales/OrderDetailPage';
import BillListPage from '@/shein-platform/modules/finance/BillListPage';
import BillDetailPage from '@/shein-platform/modules/finance/BillDetailPage';
import SheinMarketingPage from '@/shein-platform/modules/ops/SheinMarketingPage';
import SheinMarketingEnrollPage from '@/shein-platform/modules/ops/SheinMarketingEnrollPage';
import { IPE_SHEIN_BASE_PATH, IPE_SHEIN_GATE_PATH, buildSheinPath, isSheinShopTypeSlug, parseSheinShopTypeSlugFromPath } from '@/shein-platform/constants/sheinRoutes';
import { TemuWorkspaceAuthProvider } from '@/temu-platform/context/TemuWorkspaceAuthContext';
import TemuPlatformGatePage from '@/temu-platform/layout/TemuPlatformGatePage';
import TemuPlatformLayout from '@/temu-platform/layout/TemuPlatformLayout';
import TemuProductEditorLayout from '@/temu-platform/layout/TemuProductEditorLayout';
import TemuStandaloneHelpLayout from '@/temu-platform/layout/TemuStandaloneHelpLayout';
import TemuWorkspaceGuard from '@/temu-platform/components/TemuWorkspaceGuard';
import TemuProductOpsLayout from '@/temu-platform/modules/products/TemuProductOpsLayout';
import TemuProductListPage from '@/temu-platform/modules/products/ProductListPage';
import TemuProductPublishRecordsPage from '@/temu-platform/modules/products/ProductPublishRecordsPage';
import TemuProductDraftsPage from '@/temu-platform/modules/products/ProductDraftsPage';
import TemuProductPublishWizardPage from '@/temu-platform/modules/products/ProductPublishWizardPage';
import TemuProductEditPage from '@/temu-platform/modules/products/ProductEditPage';
import TemuHomePage from '@/temu-platform/modules/home/TemuHomePage';
import TemuDiscussHubPage from '@/temu-platform/modules/discuss/DiscussHubPage';
import TemuDiscussListPage from '@/temu-platform/modules/discuss/DiscussListPage';
import TemuRrpListPage from '@/temu-platform/modules/discuss/RrpListPage';
import TemuCertificateLibraryPage from '@/temu-platform/modules/certificates/CertificateLibraryPage';
import TemuSizeChartTemplatePage from '@/temu-platform/modules/templates/SizeChartTemplatePage';
import TemuProductAttributeTemplatePage from '@/temu-platform/modules/templates/ProductAttributeTemplatePage';
import TemuPhotoTemplatePage from '@/temu-platform/modules/templates/PhotoTemplatePage';
import TemuRrpTemplatePage from '@/temu-platform/modules/templates/RrpTemplatePage';
import TemuProductTemplatePage from '@/temu-platform/modules/templates/ProductTemplatePage';
import TemuProductTemplateEditorPage from '@/temu-platform/modules/templates/ProductTemplateEditorPage';
import TemuModelTemplatePage from '@/temu-platform/modules/templates/ModelTemplatePage';
import TemuWarehouseTemplatePage from '@/temu-platform/modules/templates/WarehouseTemplatePage';
import TemuFreightTemplatePage from '@/temu-platform/modules/templates/FreightTemplatePage';
import TemuNoticeHelpPage from '@/temu-platform/modules/help/TemuNoticeHelpPage';
import TemuShopInfoPage from '@/temu-platform/modules/shop/ShopInfoPage';
import TemuReturnsRoutePage from '@/temu-platform/modules/aftersales/TemuReturnsRoutePage';
import TemuReturnsDetailRoutePage from '@/temu-platform/modules/aftersales/TemuReturnsDetailRoutePage';
import TemuOrderDetailPage from '@/temu-platform/modules/aftersales/OrderDetailPage';
import TemuOrdersIndexRedirect from '@/temu-platform/modules/ops/TemuOrdersIndexRedirect';
import {
  TemuComplianceIndexRoute,
  TemuComplianceInfoRoute,
  TemuComplianceMaterialsRoute,
  TemuCompliancePhotosRoute,
  TemuComplianceCertsRoute,
  TemuProductQualRoute,
} from '@/temu-platform/modules/ops/TemuComplianceGateRoutes';
import TemuBillListPage from '@/temu-platform/modules/finance/BillListPage';
import TemuBillDetailPage from '@/temu-platform/modules/finance/BillDetailPage';
import TemuStockRoutePage from '@/temu-platform/modules/ops/TemuStockRoutePage';
import TemuMarketingEnrollPage from '@/temu-platform/modules/ops/TemuMarketingEnrollPage';
import {
  TemuFullFulfillmentPage,
  TemuFullPrintPage,
  TemuFullAftersalePage,
  TemuFullPackingPage,
  TemuMarketingPage,
  TemuMarketingEnrollsPage,
  TemuMarketingProductsPage,
  TemuFullAdsPage,
  TemuFullSalesPage,
} from '@/temu-platform/modules/ops/TemuFullPhase2Pages';
import CommercePlatformPickerPage from '@/pages/ipe/commerce/CommercePlatformPickerPage';
import {
  IPE_TEMU_BASE_PATH,
  IPE_TEMU_GATE_PATH,
  buildTemuPath,
  isTemuShopTypeSlug,
  parseTemuShopTypeSlugFromPath,
} from '@/temu-platform/constants/temuRoutes';
import { TiktokWorkspaceAuthProvider } from '@/tiktok-platform/context/TiktokWorkspaceAuthContext';
import TiktokPlatformGatePage from '@/tiktok-platform/layout/TiktokPlatformGatePage';
import TiktokPlatformLayout from '@/tiktok-platform/layout/TiktokPlatformLayout';
import TiktokProductEditorLayout from '@/tiktok-platform/layout/TiktokProductEditorLayout';
import TiktokStandaloneHelpLayout from '@/tiktok-platform/layout/TiktokStandaloneHelpLayout';
import TiktokWorkspaceGuard from '@/tiktok-platform/components/TiktokWorkspaceGuard';
import TiktokProductOpsLayout from '@/tiktok-platform/modules/products/TiktokProductOpsLayout';
import TiktokProductListPage from '@/tiktok-platform/modules/products/ProductListPage';
import TiktokProductPublishRecordsPage from '@/tiktok-platform/modules/products/ProductPublishRecordsPage';
import TiktokProductDraftsPage from '@/tiktok-platform/modules/products/ProductDraftsPage';
import TiktokProductPublishWizardPage from '@/tiktok-platform/modules/products/ProductPublishWizardPage';
import TiktokProductEditPage from '@/tiktok-platform/modules/products/ProductEditPage';
import TiktokHomePage from '@/tiktok-platform/modules/home/TiktokHomePage';
import TiktokSizeChartTemplatePage from '@/tiktok-platform/modules/templates/SizeChartTemplatePage';
import TiktokProductAttributeTemplatePage from '@/tiktok-platform/modules/templates/ProductAttributeTemplatePage';
import TiktokBrandLibraryPage from '@/tiktok-platform/modules/templates/BrandLibraryPage';
import TiktokProductTemplatePage from '@/tiktok-platform/modules/templates/ProductTemplatePage';
import TiktokProductTemplateEditorPage from '@/tiktok-platform/modules/templates/ProductTemplateEditorPage';
import TiktokWarehouseTemplatePage from '@/tiktok-platform/modules/templates/WarehouseTemplatePage';
import TiktokFreightTemplatePage from '@/tiktok-platform/modules/templates/FreightTemplatePage';
import {
  TiktokComplianceIndexRoute,
  TiktokComplianceMaterialsRoute,
  TiktokProductQualRoute,
} from '@/tiktok-platform/modules/ops/TiktokComplianceGateRoutes';
import TiktokNoticeHelpPage from '@/tiktok-platform/modules/help/TiktokNoticeHelpPage';
import TiktokShopInfoPage from '@/tiktok-platform/modules/shop/ShopInfoPage';
import TiktokReturnOrderListPage from '@/tiktok-platform/modules/aftersales/ReturnOrderListPage';
import TiktokCancellationListPage from '@/tiktok-platform/modules/aftersales/CancellationListPage';
import TiktokReturnOrderDetailPage from '@/tiktok-platform/modules/aftersales/ReturnOrderDetailPage';
import TiktokOrderDetailPage from '@/tiktok-platform/modules/aftersales/OrderDetailPage';
import TiktokOrdersIndexRedirect from '@/tiktok-platform/modules/ops/TiktokOrdersIndexRedirect';
import TiktokAnalyticsPage from '@/tiktok-platform/modules/ops/TiktokAnalyticsPage';
import TiktokPaymentsPage from '@/tiktok-platform/modules/finance/TiktokPaymentsPage';
import TiktokBillDetailPage from '@/tiktok-platform/modules/finance/BillDetailPage';
import TiktokMarketingEnrollPage from '@/tiktok-platform/modules/ops/TiktokMarketingEnrollPage';
import {
  TiktokMarketingPage,
  TiktokMarketingEnrollsPage,
  TiktokMarketingProductsPage,
  TiktokMarketingCouponsPage,
} from '@/tiktok-platform/modules/ops/TiktokFullPhase2Pages';
import {
  IPE_TIKTOK_AUTH_CODE_PATH,
  IPE_TIKTOK_BASE_PATH,
  IPE_TIKTOK_GATE_PATH,
  buildTiktokPath,
  isTiktokShopTypeSlug,
  parseTiktokShopTypeSlugFromPath,
} from '@/tiktok-platform/constants/tiktokRoutes';
import { TutorialAuthProvider } from './context/TutorialAuthContext';
import {
  IPE_LOGIN_PATH,
  IPE_POD_OAUTH_AUTHORIZE_PATH,
  IPE_WORKBENCH_PATH,
  IPE_AGENT_CHAT_PATH,
  IPE_CREDIT_CONSUMPTION_PATH,
  IPE_RECHARGE_PATH,
  IPE_PLUGIN_INSTALL_PATH,
  IPE_MANUAL_HOME_PATH,
  IPE_MANUAL_LOGIN_PATH,
  LEGACY_LOGIN_PATH,
  migrateLegacyWorkbenchPath,
} from './config/ipeRoutes';
import IpeGlobalFloatBalls from './components/ipe/IpeGlobalFloatBalls';
import IpeRechargeNavigateBootstrap from './components/ipe/IpeRechargeNavigateBootstrap';
import {
  LazyIpeWorkbenchPage,
  LazyIpeDesignPage,
  LazyImageDesignerPage,
  LazyGoodsDetailPage,
  LazyGoodsCompositeEditorPage,
  LazyPodCompositeEditorPage,
  LazyProductListingDetailPage,
  LazyPlatformListingJobDetailPage,
  LazyVideoGenerateDetailPage,
  LazyImg2ImgDetailPage,
  LazyAiCopywritingDetailPage,
  LazyAiImageDetailPage,
  LazyImageCropDetailPage,
  LazyAiModelDetailPage,
  LazyImageFissionDetailPage,
  LazyIpeWorkflowTemplateEditorPage,
  LazyIpeWorkflowBatchRunPage,
  LazyAgentChatPage,
  LazyListingTemplatePreviewPage,
  LazyImageTemplateEditorRedirect,
  LazyAdminLayout,
  LazyAdminBigScreenPage,
  LazyAdminDashboardPage,
  LazyAdminStorageCapacityPage,
  LazyAdminStorageAclPage,
  LazyAdminUsersPage,
  LazyAdminLoginEventsPage,
  LazyAdminLoginLocksPage,
  LazyAdminCreditConsumptionLogsPage,
  LazyAdminPurchaseIntentsPage,
  LazyAdminClientErrorLogsPage,
  LazyAdminTaskQueuesPage,
  LazyAdminDataGovernancePage,
  LazyAdminSheinWebhooksPage,
  LazyAdminFinancePage,
  LazyAdminMembershipPage,
  LazyAdminShopQuotaPage,
  LazyAdminStorageQuotaPage,
  LazyAdminModulesPage,
  LazyAdminActivitySettingsPage,
  LazyAdminSystemAnnouncementPage,
  LazyAdminSystemAnnouncementEditorPage,
  LazyAdminOfficialWorkflowPage,
  LazyAdminOfficialWorkflowEditorPage,
  LazyAdminOfficialImageTemplatePage,
  LazyAdminOfficialTitleTemplatePage,
  LazyAdminDesignColorLibraryPage,
  LazyAdminOfficialText2ImgPromptCasePage,
  LazyAdminOfficialImg2ImgPromptCasePage,
  LazyAdminOfficialTableTemplateRulesPage,
  LazyAdminSheinProductTemplatePage,
  LazyAdminSheinAttributeTemplatePage,
  LazyAdminSheinSizeChartTemplatePage,
  LazyAdminSheinPhotoTemplatePage,
  LazyAdminSheinCertificateTemplatePage,
  LazyAdminSheinProductTemplateEditorPage,
  LazyAdminSheinTemplatesLayout,
  LazyAdminTemuProductTemplatePage,
  LazyAdminTemuAttributeTemplatePage,
  LazyAdminTemuSizeChartTemplatePage,
  LazyAdminTemuModelTemplatePage,
  LazyAdminTemuPhotoTemplatePage,
  LazyAdminTemuCertificateTemplatePage,
  LazyAdminTemuProductTemplateEditorPage,
  LazyAdminTemuTemplatesLayout,
  LazyAdminTiktokProductTemplatePage,
  LazyAdminTiktokAttributeTemplatePage,
  LazyAdminTiktokSizeChartTemplatePage,
  LazyAdminTiktokProductTemplateEditorPage,
  LazyAdminTiktokTemplatesLayout,
  LazyAdminOfficialMaterialPage,
  LazyAdminFeaturedMaterialPage,
  LazyAdminSystemModelsPage,
  LazyAdminTutorialUsersPage,
  LazyAdminPlatformTutorialPage,
  LazyAgentPortalLayout,
  LazyAgentPortalDashboardPage,
  LazyAgentPortalModelsPage,
  LazyAgentPortalLogsPage,
  LazyAgentPortalIntegrationsPage,
  LazyAgentPortalTasksPage,
  LazyAgentPortalSystemLogsPage,
  LazyAgentPortalSidecarApisPage,
} from './routes/lazyPages';

function LegacyIpeToolsRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={migrateLegacyWorkbenchPath(location.pathname, location.search, location.hash)}
      replace
    />
  );
}

/** 旧路径 /ipe/shein/after-sales/:no → /ipe/shein/{type}/orders/returns/:no */
function LegacySheinAfterSalesDetailRedirect() {
  const { shopType, returnOrderNo } = useParams();
  const no = returnOrderNo ? encodeURIComponent(returnOrderNo) : '';
  const slug = isSheinShopTypeSlug(shopType) ? shopType : parseSheinShopTypeSlugFromPath(window.location.pathname) || 'semi';
  return <Navigate to={buildSheinPath(slug, `orders/returns/${no}`)} replace />;
}

/** 旧扁平路径 /ipe/shein/home → /ipe/shein/{type}/home */
function SheinLegacyFlatRedirect() {
  const location = useLocation();
  const rest = location.pathname.slice(IPE_SHEIN_BASE_PATH.length).replace(/^\//, '');
  const first = rest.split('/')[0] || '';
  if (!rest || first === 'gate' || first === 'help' || isSheinShopTypeSlug(first)) {
    return <Navigate to={buildSheinPath('home')} replace />;
  }
  return <Navigate to={`${buildSheinPath('')}/${rest}${location.search}${location.hash}`} replace />;
}

/** 旧路径兼容：无类型段时补上 */
function TemuLegacyFlatRedirect() {
  const location = useLocation();
  const rest = location.pathname.slice(IPE_TEMU_BASE_PATH.length).replace(/^\//, '');
  const first = rest.split('/')[0] || '';
  if (!rest || first === 'gate' || first === 'help' || isTemuShopTypeSlug(first)) {
    return <Navigate to={buildTemuPath('home')} replace />;
  }
  return <Navigate to={`${buildTemuPath('')}/${rest}${location.search}${location.hash}`} replace />;
}

function TiktokLegacyFlatRedirect() {
  const location = useLocation();
  const rest = location.pathname.slice(IPE_TIKTOK_BASE_PATH.length).replace(/^\//, '');
  const first = rest.split('/')[0] || '';
  if (!rest || first === 'gate' || first === 'help' || isTiktokShopTypeSlug(first)) {
    return <Navigate to={buildTiktokPath('home')} replace />;
  }
  return <Navigate to={`${buildTiktokPath('')}/${rest}${location.search}${location.hash}`} replace />;
}

function lazyRoute(Page, label, props = undefined) {
  const element = props ? <Page {...props} /> : <Page />;
  return (
    <RoutePageSuspense label={label}>
      {element}
    </RoutePageSuspense>
  );
}

export default function App() {
  return (
    <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
      <DocumentTitleSync />
      <AutoEllipsisTitle />
      <WorkbenchNavHandoffBootstrap />
      <IpeRechargeNavigateBootstrap />
      <IpeAuthProvider>
        <WorkspaceSwitchLockProvider>
        <IpeGlobalFloatBalls />
        <Routes>
          <Route path={IPE_LOGIN_PATH} element={<IpeLoginPage />} />
          <Route path={LEGACY_LOGIN_PATH} element={<IpeLoginPage />} />
          <Route path={IPE_POD_OAUTH_AUTHORIZE_PATH} element={<PodOAuthAuthorizePage />} />
          <Route path={IPE_TIKTOK_AUTH_CODE_PATH} element={<TiktokAuthCodePage />} />
          <Route path="/legal/terms" element={<LegalTermsPage />} />
          <Route path="/legal/privacy" element={<LegalPrivacyPage />} />
          <Route path={IPE_PLUGIN_INSTALL_PATH} element={<PluginInstallPage />} />
          <Route path={IPE_MANUAL_LOGIN_PATH} element={
            <TutorialAuthProvider>
              <TutorialManualLoginPage />
            </TutorialAuthProvider>
          } />
          <Route path={IPE_MANUAL_HOME_PATH} element={
            <TutorialAuthProvider>
              <TutorialManualLayout />
            </TutorialAuthProvider>
          }>
            <Route index element={<TutorialManualHomePage />} />
            <Route path=":slug" element={<TutorialManualNodePage />} />
          </Route>
          <Route path="/register" element={<Navigate to={IPE_LOGIN_PATH} replace />} />
          <Route path="/management/login" element={<Navigate to={LEGACY_LOGIN_PATH} replace />} />
          <Route path="/ipe-design" element={lazyRoute(LazyIpeDesignPage, '正在加载设计页…')} />
          <Route path="/funding-design" element={<Navigate to="/ipe-design" replace />} />
          <Route path="/funding-tools/*" element={<LegacyIpeToolsRedirect />} />

          <Route path="/ipe/admin/login" element={<AdminLoginPage />} />
          <Route path="/ipe/admin/crawler-workbench" element={<Navigate to="/ipe/admin/official-materials/material-recommendation" replace />} />
          <Route path="/ipe/admin/big-screen" element={lazyRoute(LazyAdminBigScreenPage, '正在加载大屏…')} />
          <Route path="/ipe/agent/login" element={<AgentPortalLoginPage />} />
          <Route path="/ipe/agent" element={lazyRoute(LazyAgentPortalLayout, '正在加载 Agent 控制台…')}>
            <Route index element={<Navigate to="/ipe/agent/dashboard" replace />} />
            <Route path="dashboard" element={lazyRoute(LazyAgentPortalDashboardPage)} />
            <Route path="models" element={lazyRoute(LazyAgentPortalModelsPage)} />
            <Route path="logs" element={lazyRoute(LazyAgentPortalLogsPage)} />
            <Route path="integrations" element={lazyRoute(LazyAgentPortalIntegrationsPage)} />
            <Route path="tasks" element={lazyRoute(LazyAgentPortalTasksPage)} />
            <Route path="system-logs" element={lazyRoute(LazyAgentPortalSystemLogsPage)} />
            <Route path="sidecar-apis" element={lazyRoute(LazyAgentPortalSidecarApisPage)} />
          </Route>
          <Route path="/ipe/admin" element={lazyRoute(LazyAdminLayout, '正在加载管理后台…')}>
            <Route index element={<Navigate to="/ipe/admin/dashboard" replace />} />
            <Route path="dashboard" element={lazyRoute(LazyAdminDashboardPage)} />
            <Route path="storage-capacity" element={lazyRoute(LazyAdminStorageCapacityPage)} />
            <Route path="storage-acl" element={lazyRoute(LazyAdminStorageAclPage)} />
            <Route path="users" element={lazyRoute(LazyAdminUsersPage)} />
            <Route path="tutorial-users" element={lazyRoute(LazyAdminTutorialUsersPage)} />
            <Route path="platform-tutorials" element={lazyRoute(LazyAdminPlatformTutorialPage)} />
            <Route path="login-events" element={lazyRoute(LazyAdminLoginEventsPage)} />
            <Route path="login-locks" element={lazyRoute(LazyAdminLoginLocksPage)} />
            <Route path="credit-consumption-logs" element={lazyRoute(LazyAdminCreditConsumptionLogsPage)} />
            <Route path="client-error-logs" element={lazyRoute(LazyAdminClientErrorLogsPage)} />
            <Route path="task-queues" element={lazyRoute(LazyAdminTaskQueuesPage)} />
            <Route path="data-governance" element={lazyRoute(LazyAdminDataGovernancePage)} />
            <Route path="platform/webhooks" element={lazyRoute(LazyAdminSheinWebhooksPage)} />
            <Route element={<AdminCommerceLayout />}>
              <Route path="purchase-intents" element={lazyRoute(LazyAdminPurchaseIntentsPage)} />
              <Route path="finance" element={lazyRoute(LazyAdminFinancePage)} />
              <Route path="membership" element={lazyRoute(LazyAdminMembershipPage)} />
              <Route path="shop-quota" element={lazyRoute(LazyAdminShopQuotaPage)} />
              <Route path="storage-quota" element={lazyRoute(LazyAdminStorageQuotaPage)} />
              <Route path="modules" element={lazyRoute(LazyAdminModulesPage)} />
            </Route>
            <Route path="activity/settings" element={lazyRoute(LazyAdminActivitySettingsPage)} />
            <Route path="activity/data" element={<Navigate to="/ipe/admin/dashboard" replace />} />
            <Route path="activities" element={<Navigate to="/ipe/admin/activity/settings" replace />} />
            <Route path="system-announcements" element={lazyRoute(LazyAdminSystemAnnouncementPage)} />
            <Route path="system-announcements/new" element={lazyRoute(LazyAdminSystemAnnouncementEditorPage)} />
            <Route path="system-announcements/:id/edit" element={lazyRoute(LazyAdminSystemAnnouncementEditorPage)} />
            <Route element={<AdminOfficialDataLayout />}>
              <Route path="official-data/workflows" element={lazyRoute(LazyAdminOfficialWorkflowPage)} />
              <Route path="official-data/workflows/edit/:draftKey" element={lazyRoute(LazyAdminOfficialWorkflowEditorPage)} />
              <Route path="official-data/title-templates" element={lazyRoute(LazyAdminOfficialTitleTemplatePage)} />
              <Route path="official-data/design-colors" element={lazyRoute(LazyAdminDesignColorLibraryPage)} />
              <Route path="official-data/text2img-prompt-cases" element={lazyRoute(LazyAdminOfficialText2ImgPromptCasePage)} />
              <Route path="official-data/img2img-prompt-cases" element={lazyRoute(LazyAdminOfficialImg2ImgPromptCasePage)} />
              <Route path="official-data/table-template-rules" element={lazyRoute(LazyAdminOfficialTableTemplateRulesPage)} />
              <Route path="official-data/image-templates" element={lazyRoute(LazyAdminOfficialImageTemplatePage)} />
              <Route path="official-data/shein-product-templates" element={lazyRoute(LazyAdminSheinTemplatesLayout)}>
                <Route index element={lazyRoute(LazyAdminSheinProductTemplatePage)} />
                <Route path="attributes" element={lazyRoute(LazyAdminSheinAttributeTemplatePage)} />
                <Route path="size-chart" element={lazyRoute(LazyAdminSheinSizeChartTemplatePage)} />
                <Route path="certificates" element={lazyRoute(LazyAdminSheinCertificateTemplatePage)} />
                <Route path="photo" element={lazyRoute(LazyAdminSheinPhotoTemplatePage)} />
              </Route>
              <Route path="official-data/temu-product-templates" element={lazyRoute(LazyAdminTemuTemplatesLayout)}>
                <Route index element={lazyRoute(LazyAdminTemuProductTemplatePage)} />
                <Route path="attributes" element={lazyRoute(LazyAdminTemuAttributeTemplatePage)} />
                <Route path="size-chart" element={lazyRoute(LazyAdminTemuSizeChartTemplatePage)} />
                <Route path="model" element={lazyRoute(LazyAdminTemuModelTemplatePage)} />
                <Route path="certificates" element={lazyRoute(LazyAdminTemuCertificateTemplatePage)} />
                <Route path="photo" element={lazyRoute(LazyAdminTemuPhotoTemplatePage)} />
              </Route>
              <Route path="official-data/tiktok-product-templates" element={lazyRoute(LazyAdminTiktokTemplatesLayout)}>
                <Route index element={lazyRoute(LazyAdminTiktokProductTemplatePage)} />
                <Route path="attributes" element={lazyRoute(LazyAdminTiktokAttributeTemplatePage)} />
                <Route path="size-chart" element={lazyRoute(LazyAdminTiktokSizeChartTemplatePage)} />
              </Route>
              <Route path="official-data/ai-inspiration" element={<Navigate to="/ipe/admin/official-materials/ai-inspiration" replace />} />
              <Route path="official-data/material-recommendation" element={<Navigate to="/ipe/admin/official-materials/material-recommendation" replace />} />
              <Route path="official-data/materials" element={<Navigate to="/ipe/admin/official-materials/material-recommendation" replace />} />
            </Route>
            <Route
              path="official-data/shein-product-templates/edit/:templateId"
              element={lazyRoute(LazyAdminSheinProductTemplateEditorPage)}
            />
            <Route
              path="official-data/temu-product-templates/edit/:templateId"
              element={lazyRoute(LazyAdminTemuProductTemplateEditorPage)}
            />
            <Route
              path="official-data/tiktok-product-templates/edit/:templateId"
              element={lazyRoute(LazyAdminTiktokProductTemplateEditorPage)}
            />
            <Route element={<AdminOfficialMaterialsLayout />}>
              <Route path="official-materials/ai-inspiration" element={lazyRoute(LazyAdminOfficialMaterialPage, undefined, { pageKey: 'ai-inspiration' })} />
              <Route path="official-materials/material-recommendation" element={lazyRoute(LazyAdminOfficialMaterialPage, undefined, { pageKey: 'material-recommendation' })} />
              <Route path="official-materials/featured" element={lazyRoute(LazyAdminFeaturedMaterialPage)} />
              <Route path="official-materials/system-models" element={lazyRoute(LazyAdminSystemModelsPage)} />
            </Route>
            <Route path="market-insights/*" element={<Navigate to="/ipe/admin/dashboard" replace />} />
            <Route path="tasks" element={<Navigate to="/ipe/agent/tasks" replace />} />
            <Route path="logs" element={<Navigate to="/ipe/agent/system-logs" replace />} />
          </Route>

          <Route element={<IpeRequireAuth />}>
            <Route path="/" element={<Navigate to={IPE_WORKBENCH_PATH} replace />} />
            <Route path={`${IPE_WORKBENCH_PATH}/workflow-template-edit/:draftKey`} element={lazyRoute(LazyIpeWorkflowTemplateEditorPage, '正在加载工作流编辑器…')} />
            <Route path={`${IPE_WORKBENCH_PATH}/workflow-batch-run`} element={lazyRoute(LazyIpeWorkflowBatchRunPage, '正在加载批量运行…')} />
            <Route path={`${IPE_WORKBENCH_PATH}/image-template-editor/:templateId`} element={lazyRoute(LazyImageTemplateEditorRedirect)} />
            <Route path={`${IPE_WORKBENCH_PATH}/image-template-editor`} element={lazyRoute(LazyImageTemplateEditorRedirect)} />
            <Route path={`${IPE_WORKBENCH_PATH}/image-designer/:templateId`} element={lazyRoute(LazyImageDesignerPage, '正在加载图片设计器…')} />
            <Route path={`${IPE_WORKBENCH_PATH}/image-designer`} element={lazyRoute(LazyImageDesignerPage, '正在加载图片设计器…')} />
            <Route path={`${IPE_WORKBENCH_PATH}/listing-template-preview/:templateId`} element={lazyRoute(LazyListingTemplatePreviewPage)} />
            <Route path={`${IPE_WORKBENCH_PATH}/goods-detail/:taskId`} element={lazyRoute(LazyGoodsDetailPage, '正在加载商品详情…')} />
            <Route path={`${IPE_WORKBENCH_PATH}/goods-composite-edit/:taskId`} element={lazyRoute(LazyGoodsCompositeEditorPage, '正在加载合成编辑器…')} />
            <Route path={`${IPE_WORKBENCH_PATH}/pod-composite-edit/:taskId`} element={lazyRoute(LazyPodCompositeEditorPage, '正在加载 POD 编辑器…')} />
            <Route path={`${IPE_WORKBENCH_PATH}/product-listing-detail/:itemId`} element={lazyRoute(LazyProductListingDetailPage, '正在加载上架详情…')} />
            <Route path={`${IPE_WORKBENCH_PATH}/listing-job-detail/:jobId`} element={lazyRoute(LazyPlatformListingJobDetailPage, '正在加载刊登任务详情…')} />
            <Route
              path={`${IPE_WORKBENCH_PATH}/title-extract-detail/:taskId?`}
              element={<Navigate to={`${IPE_WORKBENCH_PATH}?activeNav=aiCopywriting`} replace />}
            />
            <Route path={`${IPE_WORKBENCH_PATH}/image-crop-detail/:taskId`} element={lazyRoute(LazyImageCropDetailPage, '正在加载图案裁剪详情…')} />
            <Route path={`${IPE_WORKBENCH_PATH}/image-enhance-detail/:taskId`} element={lazyRoute(LazyAiImageDetailPage, '正在加载任务详情…', { navId: 'imageEnhance' })} />
            <Route path={`${IPE_WORKBENCH_PATH}/smart-cutout-detail/:taskId`} element={lazyRoute(LazyAiImageDetailPage, '正在加载任务详情…', { navId: 'smartCutout' })} />
            <Route path={`${IPE_WORKBENCH_PATH}/video-generate-detail/:taskId`} element={lazyRoute(LazyVideoGenerateDetailPage, '正在加载视频任务…')} />
            <Route path={`${IPE_WORKBENCH_PATH}/ai-image-detail/:navId/:taskId`} element={lazyRoute(LazyAiImageDetailPage, '正在加载任务详情…')} />
            <Route path={`${IPE_WORKBENCH_PATH}/ai-model-detail/:navId/:taskId`} element={lazyRoute(LazyAiModelDetailPage, '正在加载任务详情…')} />
            <Route path={`${IPE_WORKBENCH_PATH}/img2img-detail/:taskId`} element={lazyRoute(LazyImg2ImgDetailPage, '正在加载任务详情…')} />
            <Route path={`${IPE_WORKBENCH_PATH}/model-try-on-detail/:taskId`} element={lazyRoute(LazyAiModelDetailPage, '正在加载任务详情…', { navId: 'modelTryOn' })} />
            <Route path={`${IPE_WORKBENCH_PATH}/model-handheld-detail/:taskId`} element={lazyRoute(LazyAiModelDetailPage, '正在加载任务详情…', { navId: 'modelHandheld' })} />
            <Route path={`${IPE_WORKBENCH_PATH}/model-pose-detail/:taskId`} element={lazyRoute(LazyAiModelDetailPage, '正在加载任务详情…', { navId: 'modelPoseFission' })} />
            <Route path={`${IPE_WORKBENCH_PATH}/model-wear-detail/:taskId`} element={lazyRoute(LazyAiModelDetailPage, '正在加载任务详情…', { navId: 'modelUniversalWear' })} />
            <Route path={`${IPE_WORKBENCH_PATH}/ai-copywriting-detail/:taskId`} element={lazyRoute(LazyAiCopywritingDetailPage, '正在加载任务详情…')} />
            <Route path={`${IPE_WORKBENCH_PATH}/ai-outpaint-detail/:taskId`} element={lazyRoute(LazyImg2ImgDetailPage, '正在加载任务详情…', { navId: 'aiOutpaint' })} />
            <Route path={`${IPE_WORKBENCH_PATH}/ai-text-to-image-detail/:taskId`} element={lazyRoute(LazyImg2ImgDetailPage, '正在加载任务详情…', { navId: 'aiTextToImage' })} />
            <Route path={`${IPE_WORKBENCH_PATH}/stamp-extract-detail/:taskId`} element={lazyRoute(LazyAiImageDetailPage, '正在加载任务详情…', { navId: 'stampExtract' })} />
            <Route path={`${IPE_WORKBENCH_PATH}/image-fission-detail/:batchId`} element={lazyRoute(LazyImageFissionDetailPage, '正在加载裂变详情…')} />
            <Route path={IPE_AGENT_CHAT_PATH} element={lazyRoute(LazyAgentChatPage, '正在加载 Agent…')} />
            <Route path={`${IPE_AGENT_CHAT_PATH}/:conversationId`} element={lazyRoute(LazyAgentChatPage, '正在加载 Agent…')} />
            <Route path={IPE_CREDIT_CONSUMPTION_PATH} element={<CreditConsumptionPage />} />
            <Route path={IPE_RECHARGE_PATH} element={<IpeRechargePage />} />
            <Route path="/ipe/commerce" element={<CommercePlatformPickerPage />} />
            <Route path={IPE_SHEIN_GATE_PATH} element={
              <SheinWorkspaceAuthProvider>
                <SheinPlatformGatePage />
              </SheinWorkspaceAuthProvider>
            } />
            <Route path={IPE_TEMU_GATE_PATH} element={
              <TemuWorkspaceAuthProvider>
                <TemuPlatformGatePage />
              </TemuWorkspaceAuthProvider>
            } />
            <Route path={IPE_TIKTOK_GATE_PATH} element={
              <TiktokWorkspaceAuthProvider>
                <TiktokPlatformGatePage />
              </TiktokWorkspaceAuthProvider>
            } />
            <Route path={IPE_TEMU_BASE_PATH} element={
              <TemuWorkspaceAuthProvider>
                <TemuWorkspaceGuard />
              </TemuWorkspaceAuthProvider>
            }>
              <Route index element={<Navigate to={buildTemuPath('home')} replace />} />
              <Route element={<TemuStandaloneHelpLayout />}>
                <Route path="help" element={<TemuNoticeHelpPage />} />
              </Route>
              <Route path=":shopType">
                <Route element={<TemuPlatformLayout />}>
                  <Route index element={<Navigate to="home" replace />} />
                  <Route element={<TemuProductOpsLayout />}>
                    <Route path="home" element={<TemuHomePage />} />
                    <Route path="price" element={<Navigate to="discuss/cost" replace />} />
                    <Route path="stock" element={<TemuStockRoutePage />} />
                    <Route path="fulfillment" element={<Navigate to="../orders/stocking" replace />} />
                    <Route path="print" element={<Navigate to="../orders/print" replace />} />
                    <Route path="sales" element={<Navigate to="../orders/sales" replace />} />
                    <Route path="aftersale" element={<Navigate to="../orders/sample-qc" replace />} />
                    <Route path="packing" element={<Navigate to="../orders/packing" replace />} />
                    <Route path="ads" element={<Navigate to="../orders/ads" replace />} />
                    <Route path="discuss" element={<TemuDiscussHubPage />}>
                      <Route index element={<Navigate to="cost" replace />} />
                      <Route path="cost" element={<TemuDiscussListPage />} />
                      <Route path="rrp" element={<TemuRrpListPage />} />
                    </Route>
                  </Route>
                  <Route path="marketing" element={<TemuProductOpsLayout />}>
                    <Route index element={<TemuMarketingPage />} />
                    <Route path="enrolls" element={<TemuMarketingEnrollsPage />} />
                    <Route path="products" element={<TemuMarketingProductsPage />} />
                  </Route>
                  <Route path="products" element={<TemuProductOpsLayout />}>
                    <Route index element={<TemuProductListPage />} />
                    <Route path="records" element={<TemuProductPublishRecordsPage />} />
                    <Route path="drafts" element={<TemuProductDraftsPage />} />
                  </Route>
                  <Route path="compliance" element={<TemuProductOpsLayout />}>
                    <Route index element={<TemuComplianceIndexRoute />} />
                    <Route path="todos" element={<Navigate to="../home" replace />} />
                    <Route path="info" element={<TemuComplianceInfoRoute />} />
                    <Route path="photos" element={<TemuCompliancePhotosRoute />} />
                    <Route path="certs" element={<TemuComplianceCertsRoute />} />
                    <Route path="product-qual" element={<TemuProductQualRoute />} />
                    <Route path="materials" element={<TemuComplianceMaterialsRoute />} />
                    <Route path="packing" element={<Navigate to="../orders/packing" replace />} />
                    <Route path="ads" element={<Navigate to="../orders/ads" replace />} />
                    <Route path="sample-qc" element={<Navigate to="../orders/sample-qc" replace />} />
                    <Route path="library" element={<Navigate to="../templates/certificates" replace />} />
                  </Route>
                  <Route path="templates" element={<TemuProductOpsLayout />}>
                    <Route index element={<Navigate to="product" replace />} />
                    <Route path="product" element={<TemuProductTemplatePage />} />
                    <Route path="size-chart" element={<TemuSizeChartTemplatePage />} />
                    <Route path="attributes" element={<TemuProductAttributeTemplatePage />} />
                    <Route path="model" element={<TemuModelTemplatePage />} />
                    <Route path="warehouse" element={<TemuWarehouseTemplatePage />} />
                    <Route path="freight" element={<TemuFreightTemplatePage />} />
                    <Route path="photo" element={<TemuPhotoTemplatePage />} />
                    <Route path="certificates" element={<TemuCertificateLibraryPage />} />
                    <Route path="rrp" element={<TemuRrpTemplatePage />} />
                  </Route>
                  <Route path="orders" element={<TemuProductOpsLayout />}>
                    <Route index element={<TemuOrdersIndexRedirect />} />
                    <Route path="stocking" element={<TemuFullFulfillmentPage />} />
                    <Route path="print" element={<TemuFullPrintPage />} />
                    <Route path="sales" element={<TemuFullSalesPage />} />
                    <Route path="packing" element={<TemuFullPackingPage />} />
                    <Route path="ads" element={<TemuFullAdsPage />} />
                    <Route path="sample-qc" element={<TemuFullAftersalePage />} />
                    <Route path="returns" element={<TemuReturnsRoutePage />} />
                    <Route path="returns/:returnOrderNo" element={<TemuReturnsDetailRoutePage />} />
                    <Route path=":orderNo" element={<TemuOrderDetailPage />} />
                  </Route>
                  <Route element={<TemuProductOpsLayout />}>
                    <Route path="finance" element={<TemuBillListPage />} />
                    <Route path="finance/:billType/:billNo" element={<TemuBillDetailPage />} />
                    <Route path="shop" element={<TemuShopInfoPage />} />
                  </Route>
                </Route>
                {/* 发品/编辑独立壳：与工作区布局互斥，避免双顶栏嵌套 */}
                <Route element={<TemuProductEditorLayout />}>
                  <Route element={<TemuProductOpsLayout />}>
                    <Route path="products/publish" element={<TemuProductPublishWizardPage />} />
                    <Route path="products/edit/:spuName" element={<TemuProductEditPage />} />
                    <Route path="products/edit" element={<TemuProductEditPage />} />
                    <Route path="templates/product/new" element={<TemuProductTemplateEditorPage />} />
                    <Route path="templates/product/edit/:templateId" element={<TemuProductTemplateEditorPage />} />
                    <Route path="marketing/enroll/:thematicId" element={<TemuMarketingEnrollPage />} />
                  </Route>
                </Route>
              </Route>
              <Route path="*" element={<TemuLegacyFlatRedirect />} />
            </Route>
            <Route path={IPE_TIKTOK_BASE_PATH} element={
              <TiktokWorkspaceAuthProvider>
                <TiktokWorkspaceGuard />
              </TiktokWorkspaceAuthProvider>
            }>
              <Route index element={<Navigate to={buildTiktokPath('home')} replace />} />
              <Route element={<TiktokStandaloneHelpLayout />}>
                <Route path="help" element={<TiktokNoticeHelpPage />} />
              </Route>
              <Route path=":shopType">
                <Route element={<TiktokPlatformLayout />}>
                  <Route index element={<Navigate to="home" replace />} />
                  <Route element={<TiktokProductOpsLayout />}>
                    <Route path="home" element={<TiktokHomePage />} />
                    <Route path="price" element={<Navigate to="../products" replace />} />
                    <Route path="price/*" element={<Navigate to="../products" replace />} />
                    <Route path="discuss" element={<Navigate to="../products" replace />} />
                    <Route path="discuss/*" element={<Navigate to="../products" replace />} />
                    <Route path="stock" element={<Navigate to="../products" replace />} />
                    <Route path="fulfillment" element={<Navigate to="../orders" replace />} />
                    <Route path="print" element={<Navigate to="../orders" replace />} />
                    <Route path="sales" element={<Navigate to="../analytics" replace />} />
                    <Route path="aftersale" element={<Navigate to="../orders/returns" replace />} />
                    <Route path="packing" element={<Navigate to="../orders" replace />} />
                    <Route path="ads" element={<Navigate to="../analytics" replace />} />
                    <Route path="analytics" element={<TiktokAnalyticsPage />} />
                  </Route>
                  <Route path="marketing" element={<TiktokProductOpsLayout />}>
                    <Route index element={<TiktokMarketingPage />} />
                    <Route path="enrolls" element={<TiktokMarketingEnrollsPage />} />
                    <Route path="products" element={<TiktokMarketingProductsPage />} />
                    <Route path="coupons" element={<TiktokMarketingCouponsPage />} />
                  </Route>
                  <Route path="products" element={<TiktokProductOpsLayout />}>
                    <Route index element={<TiktokProductListPage />} />
                    <Route path="records" element={<TiktokProductPublishRecordsPage />} />
                    <Route path="drafts" element={<TiktokProductDraftsPage />} />
                  </Route>
                  <Route path="compliance" element={<TiktokProductOpsLayout />}>
                    <Route index element={<TiktokComplianceIndexRoute />} />
                    <Route path="product-qual" element={<TiktokProductQualRoute />} />
                    <Route path="materials" element={<TiktokComplianceMaterialsRoute />} />
                  </Route>
                  <Route path="templates" element={<TiktokProductOpsLayout />}>
                    <Route index element={<Navigate to="product" replace />} />
                    <Route path="product" element={<TiktokProductTemplatePage />} />
                    <Route path="size-chart" element={<TiktokSizeChartTemplatePage />} />
                    <Route path="attributes" element={<TiktokProductAttributeTemplatePage />} />
                    <Route path="brands" element={<TiktokBrandLibraryPage />} />
                    <Route path="model" element={<Navigate to="../product" replace />} />
                    <Route path="warehouse" element={<TiktokWarehouseTemplatePage />} />
                    <Route path="freight" element={<TiktokFreightTemplatePage />} />
                    <Route path="photo" element={<Navigate to="../product" replace />} />
                    <Route path="certificates" element={<Navigate to="../../compliance/materials" replace />} />
                    <Route path="rrp" element={<Navigate to="../product" replace />} />
                  </Route>
                  <Route path="orders" element={<TiktokProductOpsLayout />}>
                    <Route index element={<TiktokOrdersIndexRedirect />} />
                    <Route path="stocking" element={<Navigate to=".." replace />} />
                    <Route path="print" element={<Navigate to=".." replace />} />
                    <Route path="sales" element={<Navigate to="../../analytics" replace />} />
                    <Route path="packing" element={<Navigate to=".." replace />} />
                    <Route path="ads" element={<Navigate to="../../analytics" replace />} />
                    <Route path="sample-qc" element={<Navigate to="returns" replace />} />
                    <Route path="returns" element={<TiktokReturnOrderListPage />} />
                    <Route path="returns/:returnOrderNo" element={<TiktokReturnOrderDetailPage />} />
                    <Route path="cancellations" element={<TiktokCancellationListPage />} />
                    <Route path=":orderNo" element={<TiktokOrderDetailPage />} />
                  </Route>
                  <Route element={<TiktokProductOpsLayout />}>
                    <Route path="finance" element={<TiktokPaymentsPage />} />
                    <Route path="finance/:billType/:billNo" element={<TiktokBillDetailPage />} />
                    <Route path="shop" element={<TiktokShopInfoPage />} />
                  </Route>
                </Route>
                {/* 发品/编辑独立壳：与工作区布局互斥，避免双顶栏嵌套 */}
                <Route element={<TiktokProductEditorLayout />}>
                  <Route element={<TiktokProductOpsLayout />}>
                    <Route path="products/publish" element={<TiktokProductPublishWizardPage />} />
                    <Route path="products/edit/:spuName" element={<TiktokProductEditPage />} />
                    <Route path="products/edit" element={<TiktokProductEditPage />} />
                    <Route path="templates/product/new" element={<TiktokProductTemplateEditorPage />} />
                    <Route path="templates/product/edit/:templateId" element={<TiktokProductTemplateEditorPage />} />
                    <Route path="marketing/enroll/:thematicId" element={<TiktokMarketingEnrollPage />} />
                  </Route>
                </Route>
              </Route>
              <Route path="*" element={<TiktokLegacyFlatRedirect />} />
            </Route>
            <Route path={IPE_SHEIN_BASE_PATH} element={
              <SheinWorkspaceAuthProvider>
                <SheinWorkspaceGuard />
              </SheinWorkspaceAuthProvider>
            }>
              <Route index element={<Navigate to={buildSheinPath('home')} replace />} />
              <Route element={<SheinStandaloneHelpLayout />}>
                <Route path="help" element={<SheinNoticeHelpPage />} />
              </Route>
              <Route path=":shopType">
                <Route element={<SheinPlatformLayout />}>
                  <Route index element={<Navigate to="home" replace />} />
                  <Route element={<ProductOpsLayout />}>
                    <Route path="home" element={<SheinHomePage />} />
                    <Route path="discuss" element={<DiscussHubPage />}>
                      <Route index element={<Navigate to="cost" replace />} />
                      <Route path="cost" element={<DiscussListPage />} />
                      <Route path="rrp" element={<RrpListPage />} />
                    </Route>
                  </Route>
                  <Route path="marketing" element={<ProductOpsLayout />}>
                    <Route index element={<SheinMarketingPage view="activities" />} />
                    <Route path="enrolls" element={<SheinMarketingPage view="enrolls" />} />
                  </Route>
                  <Route path="products" element={<ProductOpsLayout />}>
                    <Route index element={<ProductListPage />} />
                    <Route path="records" element={<ProductPublishRecordsPage />} />
                    <Route path="drafts" element={<ProductDraftsPage />} />
                    <Route path="price-review" element={<Navigate to="../products" replace />} />
                  </Route>
                  <Route path="compliance" element={<ProductOpsLayout />}>
                    <Route index element={<Navigate to="product-qual" replace />} />
                    <Route path="todos" element={<Navigate to="../home" replace />} />
                    <Route path="product-qual" element={<ProductQualificationPage />} />
                    <Route path="materials" element={<ComplianceMaterialsPage />} />
                    <Route path="library" element={<Navigate to="../templates/certificates" replace />} />
                    <Route path="supplement" element={<Navigate to="../compliance/info" replace />} />
                    <Route path="product-compliance" element={<Navigate to="../compliance/info" replace />} />
                  </Route>
                  <Route path="templates" element={<ProductOpsLayout />}>
                    <Route index element={<Navigate to="product" replace />} />
                    <Route path="product" element={<ProductTemplatePage />} />
                    <Route path="size-chart" element={<SizeChartTemplatePage />} />
                    <Route path="attributes" element={<ProductAttributeTemplatePage />} />
                    <Route path="photo" element={<PhotoTemplatePage />} />
                    <Route path="certificates" element={<CertificateLibraryPage />} />
                    <Route path="rrp" element={<RrpTemplatePage />} />
                  </Route>
                  <Route path="certificates" element={<Navigate to="../compliance/info" replace />} />
                  <Route path="certificates/*" element={<Navigate to="../compliance/info" replace />} />
                  <Route path="orders" element={<ProductOpsLayout />}>
                    <Route index element={<OrderListPage />} />
                    <Route path="returns" element={<ReturnOrderListPage />} />
                    <Route path="returns/:returnOrderNo" element={<ReturnOrderDetailPage />} />
                    <Route path=":orderNo" element={<OrderDetailPage />} />
                  </Route>
                  <Route element={<ProductOpsLayout />}>
                    <Route path="after-sales" element={<Navigate to="../orders/returns" replace />} />
                    <Route path="after-sales/:returnOrderNo" element={<LegacySheinAfterSalesDetailRedirect />} />
                    <Route path="finance" element={<BillListPage />} />
                    <Route path="finance/:billType/:billNo" element={<BillDetailPage />} />
                    <Route path="shop" element={<ShopInfoPage />} />
                  </Route>
                </Route>
                {/* 发品/编辑独立壳：与工作区布局互斥，避免双顶栏嵌套 */}
                <Route element={<SheinProductEditorLayout />}>
                  <Route element={<ProductOpsLayout />}>
                    <Route path="products/publish" element={<ProductPublishWizardPage />} />
                    <Route path="products/edit/:spuName" element={<ProductEditPage />} />
                    <Route path="products/edit" element={<ProductEditPage />} />
                    <Route path="templates/product/new" element={<ProductTemplateEditorPage />} />
                    <Route path="templates/product/edit/:templateId" element={<ProductTemplateEditorPage />} />
                    <Route path="marketing/enroll/:activityId" element={<SheinMarketingEnrollPage />} />
                  </Route>
                </Route>
              </Route>
              <Route path="*" element={<SheinLegacyFlatRedirect />} />
            </Route>
            <Route path={`${IPE_WORKBENCH_PATH}/insights/:slug`} element={<GeekbiHotSaleRedirect />} />
            <Route path={`${IPE_WORKBENCH_PATH}/insights`} element={<GeekbiHotSaleRedirect />} />
            <Route path={`${IPE_WORKBENCH_PATH}/*`} element={lazyRoute(LazyIpeWorkbenchPage, '正在加载工作台…')} />
            <Route path="*" element={<Navigate to={IPE_WORKBENCH_PATH} replace />} />
          </Route>
        </Routes>
        </WorkspaceSwitchLockProvider>
      </IpeAuthProvider>
    </Router>
  );
}
